import { useEffect, useRef, useState } from 'react';

interface UseIsTruncatedOptions {
  enabled?: boolean;
  targetSelector?: string;
  deps?: ReadonlyArray<unknown>;
}

export const useIsTruncated = <T extends HTMLElement>({
  enabled = true,
  targetSelector,
  deps = [],
}: UseIsTruncatedOptions = {}) => {
  const elementRef = useRef<T>(null);
  const isMountedRef = useRef(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const updateIsTruncated = (nextValue: boolean) => {
      if (isMountedRef.current) {
        setIsTruncated(nextValue);
      }
    };

    if (!enabled) {
      updateIsTruncated(false);
      return;
    }

    const element = elementRef.current;
    if (!element) {
      updateIsTruncated(false);
      return;
    }

    const truncationTarget = targetSelector
      ? (element.querySelector<HTMLElement>(targetSelector) ?? element)
      : element;

    const checkTruncation = () => {
      updateIsTruncated(truncationTarget.scrollWidth > truncationTarget.clientWidth);
    };

    checkTruncation();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(checkTruncation);
      observer.observe(truncationTarget);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [enabled, targetSelector, ...deps]);

  return { elementRef, isTruncated };
};
